import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const SalesReturn = () => {
  const [step, setStep] = useState('search'); // search, select_items, confirm
  const [billSearch, setBillSearch] = useState('');
  const [bills, setBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [returnableItems, setReturnableItems] = useState([]);
  const [returnItems, setReturnItems] = useState([]);
  const [refundMode, setRefundMode] = useState('cash');
  const [refundRemarks, setRefundRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [returns, setReturns] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => { if (showHistory) fetchReturns(); }, [showHistory, fromDate, toDate]);

  const fetchReturns = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/sales-returns`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from_date: fromDate, to_date: toDate }
      });
      setReturns(resp.data.records || []);
    } catch (e) { console.error(e); }
  };

  const searchBill = async () => {
    if (!billSearch.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/bills`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: 'completed' }
      });
      const allBills = resp.data || [];
      const filtered = allBills.filter(b =>
        (b.bill_number || '').toLowerCase().includes(billSearch.toLowerCase()) ||
        (b.customer_name || '').toLowerCase().includes(billSearch.toLowerCase())
      );
      setBills(filtered.slice(0, 20));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const selectBill = async (bill) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/bills/${bill.id}/returnable-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBill(resp.data.bill);
      setReturnableItems(resp.data.returnable_items || []);
      setReturnItems(resp.data.returnable_items.map(item => ({
        ...item,
        return_qty: 0,
        return_reason: '',
        stock_action: 'current_stock',
        damage_reason: ''
      })));
      setStep('select_items');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load bill');
    } finally { setLoading(false); }
  };

  const updateReturnItem = (idx, field, value) => {
    const updated = [...returnItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setReturnItems(updated);
  };

  const getItemsToReturn = () => returnItems.filter(i => i.return_qty > 0 && i.return_reason);
  const totalRefund = getItemsToReturn().reduce((s, i) => s + (i.return_qty * i.price), 0);

  const isCreditBill = selectedBill?.payment_modes?.some(pm => pm.mode === 'credit');

  const processReturn = async () => {
    const items = getItemsToReturn();
    if (items.length === 0) { alert('Select items to return with reason'); return; }
    setProcessing(true);
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/sales-return`, {
        bill_id: selectedBill.id,
        bill_number: selectedBill.bill_number,
        items: items.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: parseFloat(i.return_qty),
          price: i.price,
          return_reason: i.return_reason,
          stock_action: i.stock_action,
          damage_reason: i.damage_reason || ''
        })),
        refund_mode: isCreditBill ? null : refundMode,
        refund_remarks: refundRemarks
      }, { headers: { Authorization: `Bearer ${token}` } });

      alert('Sales return processed successfully!');
      resetForm();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to process return');
    } finally { setProcessing(false); }
  };

  const resetForm = () => {
    setStep('search');
    setBillSearch('');
    setBills([]);
    setSelectedBill(null);
    setReturnableItems([]);
    setReturnItems([]);
    setRefundMode('cash');
    setRefundRemarks('');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div data-testid="sales-return-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Sales Return</h1>
          <p className="text-slate-500">Process returns for sold items</p>
        </div>
        <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchReturns(); }}
          className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${showHistory ? 'bg-violet-600 text-white' : 'border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          data-testid="toggle-history-btn"
        >
          {showHistory ? 'New Return' : 'Return History'}
        </button>
      </div>

      {showHistory ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            <span className="text-slate-400">to</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Return ID</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Bill No</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Customer</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Items</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600">Refund</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Mode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {returns.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No returns found</td></tr>
                ) : returns.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs">{formatDateTime(r.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs">RTN-{r.id}</td>
                    <td className="px-4 py-3 font-medium">{r.bill_number}</td>
                    <td className="px-4 py-3">{r.customer_name}</td>
                    <td className="px-4 py-3">
                      {(r.items || []).map((item, i) => (
                        <div key={i} className="text-xs">
                          {item.product_name} x{item.quantity}
                          <span className="text-slate-400 ml-1">({item.return_reason})</span>
                          {item.stock_action === 'damaged_stock' && <span className="text-red-500 ml-1">[Damaged]</span>}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-red-600">Rs.{(r.total_refund || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      {r.is_credit_bill ? (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Credit Adj.</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs capitalize">{r.refund_mode || '-'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          {/* Step 1: Search Bill */}
          {step === 'search' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Find Bill to Return</h2>
              <div className="flex gap-3">
                <input
                  type="text" value={billSearch} onChange={e => setBillSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchBill()}
                  placeholder="Search by bill number or customer name..."
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="bill-search-input"
                />
                <button onClick={searchBill} disabled={loading} className="px-6 py-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50" data-testid="search-bill-btn">
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {bills.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                  {bills.map(bill => (
                    <button key={bill.id} onClick={() => selectBill(bill)}
                      className="w-full text-left p-4 border border-slate-200 rounded-lg hover:bg-violet-50 hover:border-violet-300 transition-all"
                      data-testid={`bill-option-${bill.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-800">{bill.bill_number}</p>
                          <p className="text-sm text-slate-500">{bill.customer_name} | {formatDate(bill.created_at)}</p>
                        </div>
                        <p className="font-bold text-violet-600">Rs.{(bill.grand_total || 0).toFixed(2)}</p>
                      </div>
                      {bill.has_returns && <span className="text-xs text-amber-600 mt-1 inline-block">Has previous returns</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Items to Return */}
          {step === 'select_items' && selectedBill && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">Bill: {selectedBill.bill_number}</h2>
                    <p className="text-sm text-slate-500">{selectedBill.customer_name} | {formatDate(selectedBill.created_at)} | Total: Rs.{(selectedBill.grand_total || 0).toFixed(2)}</p>
                  </div>
                  <button onClick={resetForm} className="text-sm text-violet-600 hover:text-violet-800">Change Bill</button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
                <h3 className="font-semibold text-slate-800">Select Items to Return</h3>
                {returnItems.map((item, idx) => (
                  <div key={item.product_id} className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`return-item-${idx}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-slate-800">{item.product_name}</p>
                        <p className="text-xs text-slate-500">Sold: {item.original_qty} | Already Returned: {item.returned_qty} | Can Return: {item.returnable_qty} | Price: Rs.{item.price.toFixed(2)}</p>
                      </div>
                      {item.return_qty > 0 && (
                        <span className="text-sm font-bold text-red-600">Rs.{(item.return_qty * item.price).toFixed(2)}</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Return Qty</label>
                        <input type="number" value={item.return_qty} min={0} max={item.returnable_qty} step="1"
                          onChange={e => updateReturnItem(idx, 'return_qty', Math.min(parseFloat(e.target.value) || 0, item.returnable_qty))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          data-testid={`return-qty-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Reason *</label>
                        <select value={item.return_reason} onChange={e => updateReturnItem(idx, 'return_reason', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          data-testid={`return-reason-${idx}`}
                        >
                          <option value="">Select</option>
                          <option value="Defective">Defective</option>
                          <option value="Wrong Product">Wrong Product</option>
                          <option value="Customer Changed Mind">Customer Changed Mind</option>
                          <option value="Size/Fit Issue">Size/Fit Issue</option>
                          <option value="Quality Issue">Quality Issue</option>
                          <option value="Expired">Expired</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Stock Action</label>
                        <select value={item.stock_action} onChange={e => updateReturnItem(idx, 'stock_action', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          data-testid={`stock-action-${idx}`}
                        >
                          <option value="current_stock">Return to Stock</option>
                          <option value="damaged_stock">Mark as Damaged</option>
                        </select>
                      </div>
                      {item.stock_action === 'damaged_stock' && (
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Damage Reason</label>
                          <input type="text" value={item.damage_reason} onChange={e => updateReturnItem(idx, 'damage_reason', e.target.value)}
                            placeholder="Describe damage..."
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Refund Details */}
              {totalRefund > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-slate-800">Refund Details</h3>
                    <p className="text-xl font-bold text-red-600">Rs.{totalRefund.toFixed(2)}</p>
                  </div>

                  {isCreditBill ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700 font-medium">Credit Bill - Refund will be adjusted in Customer Ledger</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Refund Mode *</label>
                        <div className="flex gap-3">
                          {['cash', 'upi', 'bank'].map(mode => (
                            <label key={mode} className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${refundMode === mode ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                              <input type="radio" name="refund_mode" value={mode} checked={refundMode === mode} onChange={e => setRefundMode(e.target.value)} className="w-4 h-4 text-violet-600" />
                              <span className="text-sm font-medium capitalize">{mode}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Remarks</label>
                        <textarea value={refundRemarks} onChange={e => setRefundRemarks(e.target.value)} rows={2}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="Refund notes..."
                        />
                      </div>
                    </div>
                  )}

                  <button onClick={processReturn} disabled={processing || getItemsToReturn().length === 0}
                    className="w-full bg-red-600 text-white py-3 rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 transition-all"
                    data-testid="process-return-btn"
                  >
                    {processing ? 'Processing...' : `Process Return - Refund Rs.${totalRefund.toFixed(2)}`}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SalesReturn;
